//
// Copyright © 2026 Hardcore Engineering Inc.
//
// Licensed under the Eclipse Public License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License. You may
// obtain a copy of the License at https://www.eclipse.org/legal/epl-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
//
// See the License for the specific language governing permissions and
// limitations under the License.
//

/*
  Auto-scroll for every drag and drop (owner, 2026-10-26: a Backlog task
  could not be dragged up into a sprint, the page did not scroll). Browsers
  scroll an area under a drag only at its very edge, if at all, and never
  when the pointer goes past it onto a header. So while anything is dragged,
  the scroll area under the pointer, or the one the drag started in, scrolls
  as the pointer nears or passes its edge, faster the closer it gets: lists,
  the Sprints page, the board, trees, calendars, one rule for all.
*/

const EDGE = 96 // px from an edge where scrolling starts
const OUTSIDE = 160 // px past an edge where it still scrolls (a header above a list)
const MAX_STEP = 26 // px per frame at the edge or past it
// ms without a dragover: the drag has left the window or ended unseen (a held
// pointer gets one only every 350 ± 200 ms in some browsers)
const STALE = 1000

type Axis = 'x' | 'y'

let pointer: { x: number, y: number } | undefined
let over: Element | null = null
let source: Element | null = null
let lastOver = 0
let frame: number | undefined

function canScroll (el: HTMLElement, axis: Axis): boolean {
  const style = getComputedStyle(el)
  const overflow = axis === 'y' ? style.overflowY : style.overflowX
  if (overflow !== 'auto' && overflow !== 'scroll' && overflow !== 'overlay') return false
  return axis === 'y' ? el.scrollHeight > el.clientHeight : el.scrollWidth > el.clientWidth
}

/** The scroll areas around the pointer, then around the drag's start, innermost first. */
function scrollAreas (axis: Axis): HTMLElement[] {
  const result: HTMLElement[] = []
  for (const from of [over, source]) {
    for (let el = from; el != null && el !== document.documentElement; el = el.parentElement) {
      if (el instanceof HTMLElement && !result.includes(el) && canScroll(el, axis)) result.push(el)
    }
  }
  return result
}

function step (distance: number): number {
  if (distance >= EDGE || distance < -OUTSIDE) return 0
  return Math.ceil(MAX_STEP * (1 - Math.max(distance, 0) / EDGE))
}

/** Scrolls the innermost area that is near the pointer and can still move that way. */
function scrollAlong (axis: Axis, at: { x: number, y: number }): boolean {
  for (const el of scrollAreas(axis)) {
    const r = el.getBoundingClientRect()
    // Only while the pointer is beside the area, not off to its side.
    const across = axis === 'y' ? at.x >= r.left && at.x <= r.right : at.y >= r.top && at.y <= r.bottom
    if (!across) continue
    const pos = axis === 'y' ? at.y : at.x
    const start = axis === 'y' ? r.top : r.left
    const end = axis === 'y' ? r.bottom : r.right
    const offset = axis === 'y' ? el.scrollTop : el.scrollLeft
    const room = axis === 'y' ? el.scrollHeight - el.clientHeight : el.scrollWidth - el.clientWidth
    const back = step(pos - start)
    const forward = step(end - pos)
    const delta = back > 0 && offset > 0 ? -back : forward > 0 && offset < room - 1 ? forward : 0
    if (delta === 0) continue
    if (axis === 'y') el.scrollTop += delta
    else el.scrollLeft += delta
    return true
  }
  return false
}

function tick (): void {
  frame = undefined
  if (pointer === undefined) return
  if (performance.now() - lastOver > STALE) {
    stop()
    return
  }
  const moved = scrollAlong('y', pointer)
  const movedX = scrollAlong('x', pointer)
  // Keep going while something moves; the next dragover starts it again.
  if (moved || movedX) frame = requestAnimationFrame(tick)
}

function stop (): void {
  pointer = undefined
  over = null
  source = null
  if (frame !== undefined) cancelAnimationFrame(frame)
  frame = undefined
}

function onDragStart (evt: DragEvent): void {
  source = evt.target instanceof Element ? evt.target : null
}

function onDragOver (evt: DragEvent): void {
  // Some browsers report 0, 0 on the last events of a drag.
  if (evt.clientX === 0 && evt.clientY === 0) return
  pointer = { x: evt.clientX, y: evt.clientY }
  over = evt.target instanceof Element ? evt.target : null
  lastOver = performance.now()
  if (frame === undefined) frame = requestAnimationFrame(tick)
}

/** Starts auto-scroll for the whole app; returns how to stop it. */
export function installDragAutoScroll (): () => void {
  document.addEventListener('dragstart', onDragStart, true)
  document.addEventListener('dragover', onDragOver, true)
  document.addEventListener('dragend', stop, true)
  document.addEventListener('drop', stop, true)
  return () => {
    document.removeEventListener('dragstart', onDragStart, true)
    document.removeEventListener('dragover', onDragOver, true)
    document.removeEventListener('dragend', stop, true)
    document.removeEventListener('drop', stop, true)
    stop()
  }
}
